import React, { useState, useRef, useEffect } from "react";

const ClientAutocomplete = ({
  selectedClient,
  clients,
  onChange,
  disabled = false,
  className = "",
  label = "Cliente",
  onAddNewClient,
  isNewClientFormActive,
  showAddButton = true,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [filteredClients, setFilteredClients] = useState([]);
  const [isFocused, setIsFocused] = useState(false);

  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (selectedClient) {
      setInputValue(selectedClient.label);
    } else {
      setInputValue("");
    }
  }, [selectedClient]);

  const filterClients = (searchText) => {
    if (!searchText) return clients;

    const searchTerms = removeAccents(searchText.toLowerCase()).split(/\s+/);

    return clients
      .sort((a, b) => {
        const aStartsWith = removeAccents(a.label.toLowerCase()).startsWith(
          searchTerms[0]
        );
        const bStartsWith = removeAccents(b.label.toLowerCase()).startsWith(
          searchTerms[0]
        );
        if (aStartsWith && !bStartsWith) return -1;
        if (!bStartsWith && aStartsWith) return 1;
        return a.label.localeCompare(b.label);
      })
      .filter((client) => {
        const normalizedName = removeAccents(client.label.toLowerCase());
        const nameWords = normalizedName.split(/\s+/);

        return searchTerms.every((term) =>
          nameWords.some((word) => word.includes(term))
        );
      });
  };

  const removeAccents = (str) => {
    return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    setShowSuggestions(true);
    setFilteredClients(filterClients(value));
    setFocusedIndex(-1);

    if (!value) {
      onChange(null);
    }
  };

  const handleSelectClient = (client) => {
    setInputValue(client.label);
    setShowSuggestions(false);
    onChange(client);
    inputRef.current?.blur();
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setFocusedIndex((prev) =>
          prev < filteredClients.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setFocusedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (focusedIndex >= 0 && filteredClients[focusedIndex]) {
          handleSelectClient(filteredClients[focusedIndex]);
        }
        break;
      case "Escape":
        setShowSuggestions(false);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
        if (selectedClient) {
          setInputValue(selectedClient.label);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [selectedClient]);

  return (
    <div ref={wrapperRef} className={`mui-style-autocomplete ${className}`}>
      {!isNewClientFormActive && (
        <div
          className={`mui-style-input-container ${isFocused ? "focused" : ""} ${
            inputValue ? "has-value" : ""
          }`}
        >
          <input
            ref={inputRef}
            type="text"
            className="mui-style-input"
            value={inputValue}
            onChange={handleInputChange}
            onFocus={() => {
              setIsFocused(true);
              setShowSuggestions(true);
              setFilteredClients(filterClients(inputValue));
            }}
            onBlur={() => setIsFocused(false)}
            onKeyDown={handleKeyDown}
            disabled={disabled}
          />
          <label className="mui-style-label">{label}*</label>
        </div>
      )}
      {showAddButton && (
        <button
          type="button"
          className={`mui-style-add-client-btn ${
            isNewClientFormActive ? "active" : ""
          }`}
          onClick={onAddNewClient}
          title={
            isNewClientFormActive
              ? "Fechar formulário"
              : "Adicionar novo cliente"
          }
        >
          {isNewClientFormActive ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M5 12h14" />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M12 5v14M5 12h14" />
            </svg>
          )}
        </button>
      )}

      {showSuggestions && filteredClients.length > 0 && (
        <div className="mui-style-suggestions">
          {filteredClients.map((client, index) => {
            const sameNameClients = filteredClients.filter(
              (c) => c.label === client.label
            );
            const isDuplicate = sameNameClients.length > 1;

            return (
              <div
                key={client.id}
                className={`mui-style-suggestion ${
                  focusedIndex === index ? "focused" : ""
                }`}
                onClick={() => handleSelectClient(client)}
              >
                <div>
                  {client.label}
                  {isDuplicate && (
                    <span className="mui-style-client-id">
                      ID: #{client.id.slice(-4)}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ClientAutocomplete;
